"use client";

import { format } from "date-fns";
import { enUS, fi as fiLocale } from "date-fns/locale";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@heroui/react";
import { useLocale, useTranslations } from "next-intl";
import type { ReactNode } from "react";
import {
  LuCalendar,
  LuCheck,
  LuChevronDown,
  LuClock,
  LuFlag,
  LuTrophy,
  LuX,
} from "react-icons/lu";

export type ActionBarState =
  | {
      kind: "schedule-needed";
      opponentTeamName: string;
      deadline: Date;
      /** Set when this is a Braveheart challenge the user can still decline. */
      canDeclineBraveheart?: boolean;
    }
  | {
      kind: "scheduled";
      opponentTeamName: string;
      scheduledAt: Date;
      venue?: string;
    }
  | {
      kind: "result-needed";
      opponentTeamName: string;
    };

export type ActionBarHandlers = {
  /** Schedule modal (schedule-needed). */
  onSetDate?: () => void;
  /** Walkover modal — available on every state as the destructive escape. */
  onForfeit?: () => void;
  /** Schedule modal in reschedule mode (scheduled state). */
  onChangeDate?: () => void;
  /** Match-result modal (result-needed). */
  onEnterResult?: () => void;
  /** Braveheart decline (schedule-needed, only when canDeclineBraveheart). */
  onDeclineBraveheart?: () => void;
  /** Disabled state while a Firestore mutation is in flight. */
  disabled?: boolean;
};

type Props = {
  state: ActionBarState;
} & ActionBarHandlers;

type ActionVariant = "primary" | "success" | "danger";

type ActionItem = {
  key: string;
  variant: ActionVariant;
  icon: ReactNode;
  label: string;
  onClick?: () => void;
};

/**
 * Contextual action bar shown beneath the tournament headers whenever the
 * user's team has an in-flight match. Three visual states encode urgency:
 * gold (action needed), blue (informational), green (almost done).
 *
 * Mobile collapses the actions into a single dropdown anchored to the
 * primary action; desktop keeps them inline.
 */
export function ActionBar({ state, ...handlers }: Props) {
  const t = useTranslations("actionBar");
  const variant = getVariant(state.kind);
  const actions = getActions(state, handlers, t);
  const primary = actions[actions.length - 1];

  return (
    <div
      role="status"
      className={cx(
        "relative border-y",
        variant.bg,
        variant.border,
        "before:absolute before:bottom-0 before:left-0 before:top-0 before:w-[3px]",
        variant.accentBefore,
      )}
    >
      <div className="mx-auto flex max-w-[1400px] flex-row items-center gap-3 px-4 py-3 md:gap-6 md:px-6">
        {/* Left — status icon */}
        <div
          className={cx(
            "flex size-8 shrink-0 items-center justify-center rounded-full border md:size-[38px]",
            variant.iconBg,
            variant.iconBorder,
            variant.iconText,
          )}
        >
          {renderIcon(state.kind)}
        </div>

        {/* Middle — label + title */}
        <div className="min-w-0 flex-1">
          <div
            className={cx(
              "font-display text-[9px] font-semibold uppercase tracking-[0.18em] opacity-85 md:text-[10px]",
              variant.labelText,
            )}
          >
            {t(`${state.kind}.label`)}
          </div>
          <div className="mt-0.5 text-[12.5px] font-semibold leading-tight text-tn-text-primary md:text-[14px] md:leading-snug">
            <Title state={state} strongClass={variant.strongText} />
          </div>
        </div>

        {/* Right — mobile dropdown */}
        {primary && (
          <div className="shrink-0 md:hidden">
            <Dropdown
              placement="bottom-end"
              shouldBlockScroll={false}
              portalContainer={
                typeof window !== "undefined" ? document.body : undefined
              }
            >
              <DropdownTrigger>
                <button
                  type="button"
                  aria-label={primary.label}
                  disabled={handlers.disabled}
                  className={cx(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-2.5 font-display transition-all",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    triggerStyle(primary.variant),
                  )}
                >
                  <span className="flex size-[14px] items-center justify-center">
                    {primary.icon}
                  </span>
                  <LuChevronDown className="size-[14px]" aria-hidden />
                </button>
              </DropdownTrigger>
              <DropdownMenu aria-label={t(`${state.kind}.label`)}>
                {actions.map((action) => (
                  <DropdownItem
                    key={action.key}
                    onPress={action.onClick}
                    isDisabled={handlers.disabled}
                    startContent={
                      <span className="flex size-[14px] items-center justify-center">
                        {action.icon}
                      </span>
                    }
                    color={action.variant === "danger" ? "danger" : "default"}
                    className={cx(
                      action.variant === "danger" && "text-danger",
                    )}
                  >
                    {action.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}

        {/* Right — desktop inline buttons */}
        <div className="hidden md:flex md:shrink-0 md:items-center md:gap-2">
          {actions.map((action) => (
            <Btn
              key={action.key}
              variant={action.variant}
              icon={action.icon}
              onClick={action.onClick}
              disabled={handlers.disabled}
            >
              {action.label}
            </Btn>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Subcomponents
// ─────────────────────────────────────────────────────────────────────────

function Title({
  state,
  strongClass,
}: {
  state: ActionBarState;
  strongClass: string;
}) {
  const t = useTranslations("actionBar");
  const locale = useLocale();
  const dateLocale = locale === "fi" ? fiLocale : enUS;

  switch (state.kind) {
    case "schedule-needed": {
      const deadlineText = format(state.deadline, "d.M.yyyy", {
        locale: dateLocale,
      });
      return (
        <>
          {t("schedule-needed.titleBefore")}
          <strong className={cx("font-semibold", strongClass)}>
            {state.opponentTeamName}
          </strong>
          {t("schedule-needed.titleAfter", { deadline: deadlineText })}
        </>
      );
    }
    case "scheduled": {
      const dateText = format(state.scheduledAt, "d.M. 'klo' HH:mm", {
        locale: dateLocale,
      });
      return (
        <>
          <strong className={cx("font-semibold", strongClass)}>
            {dateText}
          </strong>
          {state.venue && <> · {state.venue}</>}
          {" — "}
          {t("scheduled.against")}{" "}
          <strong className={cx("font-semibold", strongClass)}>
            {state.opponentTeamName}
          </strong>
        </>
      );
    }
    case "result-needed":
      return (
        <>
          {t("result-needed.titleBefore")}
          <strong className={cx("font-semibold", strongClass)}>
            {state.opponentTeamName}
          </strong>
          {t("result-needed.titleAfter")}
        </>
      );
  }
}

// Action descriptors are built once and consumed by both the desktop button
// row and the mobile dropdown — the last entry is treated as the primary
// (drives the dropdown trigger's color + icon).
function getActions(
  state: ActionBarState,
  handlers: ActionBarHandlers,
  t: ReturnType<typeof useTranslations>,
): ActionItem[] {
  switch (state.kind) {
    case "schedule-needed": {
      const items: ActionItem[] = [];
      if (state.canDeclineBraveheart) {
        items.push({
          key: "declineBraveheart",
          variant: "danger",
          icon: <LuX />,
          label: t("actions.declineBraveheart"),
          onClick: handlers.onDeclineBraveheart,
        });
      }
      items.push({
        key: "forfeit",
        variant: "danger",
        icon: <LuFlag />,
        label: t("actions.forfeit"),
        onClick: handlers.onForfeit,
      });
      items.push({
        key: "setDate",
        variant: "primary",
        icon: <LuCalendar />,
        label: t("actions.setDate"),
        onClick: handlers.onSetDate,
      });
      return items;
    }
    case "scheduled":
      return [
        {
          key: "forfeit",
          variant: "danger",
          icon: <LuFlag />,
          label: t("actions.forfeit"),
          onClick: handlers.onForfeit,
        },
        {
          key: "changeDate",
          variant: "primary",
          icon: <LuCalendar />,
          label: t("actions.changeDate"),
          onClick: handlers.onChangeDate,
        },
      ];
    case "result-needed":
      return [
        {
          key: "forfeit",
          variant: "danger",
          icon: <LuFlag />,
          label: t("actions.forfeit"),
          onClick: handlers.onForfeit,
        },
        {
          key: "enterResult",
          variant: "success",
          icon: <LuCheck />,
          label: t("actions.enterResult"),
          onClick: handlers.onEnterResult,
        },
      ];
  }
}

type BtnProps = {
  variant: ActionVariant | "ghost";
  icon: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
};

function Btn({ variant, icon, onClick, disabled, children }: BtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border font-display text-[10px] font-bold uppercase tracking-[0.08em] transition-all",
        "px-3 py-2.5 md:rounded-lg md:px-3.5 md:py-2 md:text-[11px] md:tracking-[0.1em]",
        "disabled:cursor-not-allowed disabled:opacity-50",
        variant === "primary" &&
          "border-transparent bg-gradient-to-br from-tn-gold-soft to-tn-gold text-amber-950 shadow-[0_2px_8px_rgba(250,204,21,0.3),inset_0_1px_0_rgba(255,255,255,0.4)] hover:-translate-y-px hover:shadow-[0_4px_14px_rgba(250,204,21,0.4),inset_0_1px_0_rgba(255,255,255,0.4)]",
        variant === "success" &&
          "border-transparent bg-gradient-to-br from-emerald-300 to-tn-green text-emerald-950 shadow-[0_2px_8px_rgba(52,211,153,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] hover:-translate-y-px",
        variant === "danger" &&
          "border-tn-red/40 bg-tn-red/[0.08] text-tn-red hover:border-tn-red/60 hover:bg-tn-red/[0.12]",
        variant === "ghost" &&
          "border-tn-border-strong bg-transparent text-tn-text-secondary hover:bg-tn-elevated hover:text-tn-text-primary",
      )}
    >
      <span className="flex size-[11px] items-center justify-center md:size-[13px]">
        {icon}
      </span>
      <span>{children}</span>
    </button>
  );
}

function triggerStyle(variant: ActionVariant): string {
  switch (variant) {
    case "primary":
      return "border border-transparent bg-gradient-to-br from-tn-gold-soft to-tn-gold text-amber-950 shadow-[0_2px_8px_rgba(250,204,21,0.3),inset_0_1px_0_rgba(255,255,255,0.4)]";
    case "success":
      return "border border-transparent bg-gradient-to-br from-emerald-300 to-tn-green text-emerald-950 shadow-[0_2px_8px_rgba(52,211,153,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]";
    case "danger":
      return "border border-tn-red/40 bg-tn-red/[0.12] text-tn-red";
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Variant tokens
// ─────────────────────────────────────────────────────────────────────────

type Variant = {
  bg: string;
  border: string;
  accentBefore: string;
  iconBg: string;
  iconBorder: string;
  iconText: string;
  labelText: string;
  strongText: string;
};

function getVariant(kind: ActionBarState["kind"]): Variant {
  switch (kind) {
    case "schedule-needed":
      return {
        bg: "bg-tn-bg-deep bg-gradient-to-b from-tn-gold/[0.07] to-tn-gold/[0.02]",
        border: "border-tn-gold/20",
        accentBefore:
          "before:bg-gradient-to-b before:from-tn-gold-soft before:to-tn-gold before:shadow-[0_0_12px_rgba(250,204,21,0.4)]",
        iconBg: "bg-gradient-to-br from-tn-gold/20 to-tn-gold/[0.08]",
        iconBorder: "border-tn-gold/30",
        iconText: "text-tn-gold",
        labelText: "text-tn-gold",
        strongText: "text-tn-gold",
      };
    case "scheduled":
      return {
        bg: "bg-tn-bg-deep bg-gradient-to-b from-tn-blue/[0.07] to-tn-blue/[0.02]",
        border: "border-tn-blue/20",
        accentBefore:
          "before:bg-gradient-to-b before:from-blue-300 before:to-tn-blue before:shadow-[0_0_12px_rgba(96,165,250,0.4)]",
        iconBg: "bg-gradient-to-br from-tn-blue/20 to-tn-blue/[0.08]",
        iconBorder: "border-tn-blue/30",
        iconText: "text-tn-blue",
        labelText: "text-tn-blue",
        strongText: "text-tn-blue",
      };
    case "result-needed":
      return {
        bg: "bg-tn-bg-deep bg-gradient-to-b from-tn-green/[0.07] to-tn-green/[0.02]",
        border: "border-tn-green/20",
        accentBefore:
          "before:bg-gradient-to-b before:from-emerald-300 before:to-tn-green before:shadow-[0_0_12px_rgba(52,211,153,0.4)]",
        iconBg: "bg-gradient-to-br from-tn-green/20 to-tn-green/[0.08]",
        iconBorder: "border-tn-green/30",
        iconText: "text-tn-green",
        labelText: "text-tn-green",
        strongText: "text-tn-green",
      };
  }
}

function renderIcon(kind: ActionBarState["kind"]): ReactNode {
  switch (kind) {
    case "schedule-needed":
      return <LuCalendar className="size-[15px] md:size-[18px]" />;
    case "scheduled":
      return <LuClock className="size-[15px] md:size-[18px]" />;
    case "result-needed":
      return <LuTrophy className="size-[15px] md:size-[18px]" />;
  }
}

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}
